import api from "../api";
import ApiConstant from "../../constants/api.constants";
import { Module } from "../../models/menu/menu.model";

const MENU_CACHE_KEY = "userMenuCache";
const MENU_CACHE_TIMESTAMP_KEY = "userMenuCacheTimestamp";
const CACHE_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours

let menuCache: Module[] | null = null;
let menuPromise: Promise<Module[]> | null = null;

const isCacheValid = (): boolean => {
  const timestamp = localStorage.getItem(MENU_CACHE_TIMESTAMP_KEY);
  return timestamp ? Date.now() - parseInt(timestamp, 10) < CACHE_DURATION_MS : false;
};

const getFromLocalStorage = (): Module[] | null => {
  try {
    const cachedData = localStorage.getItem(MENU_CACHE_KEY);
    if (cachedData) {
      const modules: Module[] = JSON.parse(cachedData);
      return Array.isArray(modules) && modules.length > 0 ? modules : null;
    }
  } catch (error) {
    clearLocalStorage();
  }
  return null;
};

const saveToLocalStorage = (modules: Module[]): void => {
  try {
    localStorage.setItem(MENU_CACHE_KEY, JSON.stringify(modules));
    localStorage.setItem(MENU_CACHE_TIMESTAMP_KEY, Date.now().toString());
  } catch (error) {
    // Silent fail - cache is optional
  }
};

const clearLocalStorage = (): void => {
  localStorage.removeItem(MENU_CACHE_KEY);
  localStorage.removeItem(MENU_CACHE_TIMESTAMP_KEY);
};

const fetchFromApi = async (): Promise<Module[]> => {
  const response = await api.get<Module[]>(ApiConstant.getUserMenusAsync);
  
  if (!response.data) {
    throw new Error("No menu data received from API");
  }

  const modules = Array.isArray(response.data) ? response.data : [];

  menuCache = modules;
  saveToLocalStorage(modules);
  
  return modules;
};

const getUserMenusAsync = async (forceRefresh: boolean = false): Promise<Module[]> => {
  if (!forceRefresh) {
    // Check in-memory cache
    if (menuCache) {
      return menuCache;
    }

    // Check localStorage cache
    if (isCacheValid()) {
      const cachedModules = getFromLocalStorage();
      if (cachedModules) {
        menuCache = cachedModules;
        return cachedModules;
      }
    } else {
      clearLocalStorage();
    }

    // Return in-flight request if exists
    if (menuPromise) {
      return menuPromise;
    }
  }

  try {
    menuPromise = fetchFromApi();
    const result = await menuPromise;
    menuPromise = null;
    return result;
  } catch (error) {
    menuPromise = null;
    console.error("[MenuService] API fetch failed:", error);
    throw error;
  }
};

const clearMenuCache = (): void => {
  menuCache = null;
  menuPromise = null;
  clearLocalStorage();
};
const MenuService = {
  getUserMenusAsync,
  clearMenuCache,
};

export default MenuService;
